import { Component, inject, OnDestroy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { HttpService } from '@static/services/http.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { HTTP_INVALID_PERMISSIONS_ERROR } from '@static/constants';
import { SnackService } from '@static/services/snack.service';
import { environment } from '@environments/environment';
import { DecimalPipe } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatTooltipModule } from "@angular/material/tooltip";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { PortfolioService } from '@shared/services/portfolio.service';
import { Helper } from '@static/helpers';

export interface IProjectUploadData<T> {
  formData: FormData | null | undefined,
  dataCallback: (res: T) => void
  autoClose?: boolean,
};

export interface IProjectUploadState<T> {
  status: number;
  data: T;
  percent: number;
  stage: string;
};

export interface IProjectUploadSelect {
  proj_id: string;
  proj_short_name: string;
  existing: string;
  link_proj: boolean;
  selected: boolean;
};

interface IUploadState {
  timestamp: number;
  percentage: number;
}

@Component({
  selector: 'app-project-upload',
  imports: [
    MatButtonModule,
    MatDialogActions,
    MatDialogClose,
    MatDialogTitle,
    MatDialogContent,
    MatTableModule,
    MatCheckboxModule,
    MatBadgeModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    FormsModule,
    ReactiveFormsModule,
    DecimalPipe,
  ],
  templateUrl: './project-upload.component.html',
  styleUrl: './project-upload.component.scss',
  providers: [
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } }
  ]
})
export class ProjectUploadComponent<T> implements OnDestroy {
  public dialogRef: MatDialogRef<ProjectUploadComponent<T>> = inject(MatDialogRef<ProjectUploadComponent<T>>);
  private _snackService: SnackService = inject(SnackService);
  private _httpService: HttpService = inject(HttpService);
  protected _portfolioService: PortfolioService = inject(PortfolioService);
  public data: IProjectUploadData<T> = inject(MAT_DIALOG_DATA);

  public loaded: boolean;
  public loading: boolean;

  public precheckLoading: boolean;
  public precheckComplete: boolean;
  public precheckLoaded: boolean;
  public precheckResult: IProjectUploadSelect[];
  protected dataSource: IProjectUploadSelect[];

  public loadingMessage: string;
  private _timer?: number;
  public errorStatus: string;
  public errorMsg: string;
  private _cancelled: Subject<void>;
  private _projectId?: string;
  public loadedProjects?: T;
  public loadingProgress: number;
  private _previousProgress?: number;
  private _currentTimeout: number;

  private _durations: IUploadState[];
  public durationEstimate?: number;
  public elapsedTime?: number;
  private _elapsedTimer?: number;
  protected applyPortfolio: FormControl;
  protected showLinked: FormControl;
  protected skipArchive: FormControl;

  constructor() {
    this.precheckComplete = false;
    this.precheckLoaded = false;
    this.precheckLoading = true;
    this.precheckResult = [];
    this.dataSource = [];

    this.loaded = false;
    this.loading = false;
    this.loadingMessage = '';
    this.loadingProgress = 0;
    this.errorStatus = '';
    this.errorMsg = ''
    this._cancelled = new Subject<void>();
    this._currentTimeout = 1000;
    this.loadedProjects = undefined;
    this._durations = [];

    this.applyPortfolio = new FormControl(this._portfolioService.selectedPortfolio());
    this.showLinked = new FormControl(false);
    this.skipArchive = new FormControl(false);

    if (this.data.formData) {
      // Validate project uploads
      this._httpService.postP<IProjectUploadSelect[]>(`${environment.backendUri}/project/preupload`, this.data.formData)
        .then((res: IProjectUploadSelect[]) => {
          this.precheckLoaded = true;
          this.precheckLoading = false;
          res.forEach((r) => r.selected = (r.link_proj ? false : true));
          this.precheckResult = res;
          this.filterData();
        }).catch((err: HttpErrorResponse) => this._handleError(err));
    }
  }

  /**
   * Check if any of the projects are selected
   *
   * @returns if any projects are selected
   */
  public checkSomeSelected(): boolean {
    return this.dataSource.some((r) => r.selected);
  }

  /**
   * Filter the list of visible projects
   */
  public filterData(): void {
    this.dataSource = this.precheckResult.filter((r: IProjectUploadSelect) => {
      return this.showLinked.value || !r.link_proj;
    });
  }

  /**
   * Start the file upload
   */
  public startUpload(): void {
    this.precheckComplete = true;

    if (this.data.formData) {
      this.loading = true;
      this.loadingMessage = 'Uploading Project File...';

      // Add to form
      const projectSelectedIds = this.dataSource.filter((p) => p.selected).map((p) => p.proj_id);
      this.data.formData.append('opt', projectSelectedIds.join(","));
      if (Helper.isDefined(this.applyPortfolio.value)) {
        this.data.formData.append('portfolioID', this.applyPortfolio.value);
      }
      this.data.formData.append('skipArchive', this.skipArchive.value);

      // Set initial duration marker
      this._durations.push({
        timestamp: Math.floor(new Date().getTime() / 1000),
        percentage: 0
      });
      // Set elapsed duration update
      this._elapsedTimer = window.setInterval(() => {
        this.elapsedTime = Math.floor(new Date().getTime() / 1000) - this._durations[0].timestamp;
      }, 1000);

      const uri = `${environment.backendUri}/project/upload`;
      this._httpService.postP<string>(uri, this.data.formData, {responseType: 'text'}, this._cancelled)
        .then((id: string) => {
          this._projectId = id;
          this.loadingMessage = 'File uploaded, processing projects...';

          this._timer = window.setTimeout(() => {
            this._statusUpdate();
          }, this._currentTimeout);
        }).catch((err: HttpErrorResponse) => this._handleError(err));
    }
  }

  /**
   * Check the upload status
   */
  private _statusUpdate(): void {
    this._httpService.getP<IProjectUploadState<T>>(`${environment.backendUri}/project/${this._projectId}/upload/state`)
      .then((res: IProjectUploadState<T>) => {
        this.loadingMessage = res.stage;
        this.loadingProgress = res.percent;

        // Handle update changes
        if (res.status === 0 || res.status === 1) {
          if (res.percent === this._previousProgress) {
            this._currentTimeout = Math.min(this._currentTimeout + 2000, 15000);
          } else {
            this._recordPercentageUpdate(res.percent);
            this._previousProgress = res.percent;
            this._currentTimeout = 1000;
          }
          this._timer = window.setTimeout(() => {
            this._statusUpdate();
          }, this._currentTimeout);
        }
        // Handle complete request
        if (res.status === 2) this._loadComplete(res.data);
        // Handle failed request
        if (res.status === 3) this._handleError(res.data as HttpErrorResponse);
      })
      .catch((err: HttpErrorResponse) => this._handleError(err));
  }

  /**
   * Record updates to the percentage complete for a estimate update
   *
   * @param per percentage complete
   */
  private _recordPercentageUpdate(per: number): void {
    const curTime = Math.floor(new Date().getTime() / 1000);
    this._durations.push({
      timestamp: curTime,
      percentage: per
    });
    const duration = curTime - this._durations[0].timestamp;
    this.durationEstimate = duration / (per / 100);
  }

  /**
   * Handle error response from the loading status
   *
   * @param err error
   */
  private _handleError(err: HttpErrorResponse): void {
    this.precheckComplete = true;
    this.loading = false;
    if (this._timer) window.clearTimeout(this._timer);
    if (this._elapsedTimer) window.clearInterval(this._elapsedTimer);
    if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) {
      this._snackService.openErrorSnack('Insufficient permissions for this project', err);
    }
    this.errorStatus = `${err.status.toString()}: ${err.statusText}`;
    this.errorMsg = err.error.detail;
  }

  /**
   * Once loading is complete clear the interval and set the loaded data
   *
   * @param res upload result
   */
  private _loadComplete(res: T): void {
    if (this._timer) window.clearTimeout(this._timer);
    if (this._elapsedTimer) window.clearInterval(this._elapsedTimer);
    this.loaded = true;
    this.loadedProjects = res;
    this.data.dataCallback(res);
    if (this.data.autoClose) this.close();
  }

  /**
   * Close the dialog
   */
  close(): void {
    if (this._elapsedTimer) window.clearInterval(this._elapsedTimer);
    if (this._projectId) {
      this._httpService.getP(`${environment.backendUri}/project/${this._projectId}/upload/cancel`)
        .then(() => {
          this._cancelled.next();
          this.dialogRef.close(this.loadedProjects);
        })
        .catch((err: HttpErrorResponse) => {
          if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) {
            this._snackService.openErrorSnack('Insufficient permissions for this project', err);
          }
          this.errorStatus = `${err.status.toString()}: ${err.statusText}`;
          this.errorMsg = err.error.detail;
        })
        .finally(() => {
          if (this._timer) window.clearTimeout(this._timer);
        });
    } else {
      this.dialogRef.close(this.loadedProjects);
    }
  }

  ngOnDestroy(): void {
    if (this._timer) window.clearTimeout(this._timer);
    if (this._elapsedTimer) window.clearInterval(this._elapsedTimer);
  }
}
